import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import { ACTUALLY_SUPPORTED_LANGUAGES, i18n } from '@splitfiesta/i18n';
import { changePassword } from '@splitfiesta/platform-web/auth/cognito';
import type {
  AccountDeletionConfirmDto,
  AccountDeletionConfirmResponseDto,
  AppleRevocationRequestDto,
  AppleRevocationResponseDto,
  DeletionEmailLanguage,
  UpdateProfileDto,
  UpdateSettingsDto,
  UserResponseDto,
} from '@splitfiesta/shared';

import { useApi } from '@/hooks/useApi';
import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';

import { isUserResponse } from '@/utils/typeGuards';

// Fetch user profile
export function useUserProfileQuery(enabled = true) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.userProfile(),
    queryFn: async () => {
      const response = await api.user.getProfile();
      if (!isUserResponse(response.data)) {
        throw new Error('Invalid user response');
      }
      return response.data;
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Update user profile mutation
export function useUpdateProfileMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const updateUser = useAuthStore(state => state.updateUser);

  return useMutation({
    mutationFn: async (data: UpdateProfileDto) => {
      const response = await api.user.updateProfile(data);
      return response.data as UserResponseDto;
    },
    onSuccess: updatedUser => {
      // Update the user in auth store
      updateUser(updatedUser);
      // Update the cached profile
      queryClient.setQueryData(queryKeys.userProfile(), updatedUser);
      // Invalidate current user query if it exists
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUser() });
      // Invalidate activity feed to refresh user profile images
      queryClient.invalidateQueries({ queryKey: ['activityFeed'] });
    },
  });
}

// Fetch user settings
export function useUserSettingsQuery(enabled = true) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.userSettings(),
    queryFn: async () => {
      const response = await api.user.getSettings();
      return response.data;
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Update user settings mutation
export function useUpdateSettingsMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const updateUser = useAuthStore(state => state.updateUser);

  return useMutation({
    mutationFn: async (data: UpdateSettingsDto) => {
      const response = await api.user.updateSettings(data);
      return response.data;
    },
    onSuccess: updatedSettings => {
      // Update the cached settings
      queryClient.setQueryData(queryKeys.userSettings(), updatedSettings);
      // Invalidate user profile as it might be affected
      queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });

      // IMPORTANT: Update the auth store with new settings
      // Check if updatedSettings has the data property (SuccessResponseDto) or is the direct data
      const settingsData =
        'data' in updatedSettings ? updatedSettings.data : updatedSettings;
      updateUser({
        settings: {
          currency: settingsData.currency,
          language: settingsData.language,
          exampleGroupHidden: settingsData.exampleGroupHidden,
        },
      });
    },
  });
}

// Change password mutation - uses Cognito directly
export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: async ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => {
      await changePassword(currentPassword, newPassword);
      return { success: true };
    },
  });
}

/**
 * Account deletion (store-compliance item 9). Requesting only sends the
 * confirmation email — nothing is scheduled until the emailed link is
 * confirmed on /account/deletion, so there is no store write here.
 */
/** Narrows any UI-language string to the deletion-email locale union. */
export function toDeletionEmailLanguage(
  lang?: string
): DeletionEmailLanguage | undefined {
  return lang &&
    (ACTUALLY_SUPPORTED_LANGUAGES as readonly string[]).includes(lang)
    ? (lang as DeletionEmailLanguage)
    : undefined;
}

export function useRequestAccountDeletionMutation() {
  const api = useTypedApi();

  return useMutation({
    mutationFn: async () => {
      // The UI language rides along so the emailed link is localized even
      // for accounts without an explicit language setting. Read from the
      // singleton (non-reactive is fine — the mutation fires on click).
      await api.user.requestDeletion(toDeletionEmailLanguage(i18n.language));
    },
  });
}

/**
 * Explicitly cancels a scheduled deletion. Web registers no query under
 * queryKeys.currentUser() (SessionValidator syncs once per mount), so
 * invalidation alone would be a no-op — the auth-store write-through IS
 * what clears the grace-window banner.
 */
export function useCancelAccountDeletionMutation() {
  const api = useTypedApi();
  const updateUser = useAuthStore(state => state.updateUser);

  return useMutation({
    mutationFn: async () => {
      await api.user.cancelDeletion();
    },
    onSuccess: () => {
      updateUser({ deletionEffectiveAt: undefined });
    },
  });
}

/**
 * Confirms a deletion via the emailed single-use token. PUBLIC — the
 * /account/deletion landing page runs logged-out, so the POST skips auth
 * and neutralizes useApi's 401 → /login redirect (an expired token must
 * never bounce a visitor to the login page).
 */
export function useConfirmAccountDeletionMutation() {
  const { post } = useApi();
  const updateUser = useAuthStore(state => state.updateUser);

  return useMutation({
    mutationFn: async ({ token, lang }: { token: string; lang?: string }) => {
      const response = await post<
        AccountDeletionConfirmResponseDto,
        AccountDeletionConfirmDto
      >(
        '/user/deletion-confirm',
        // lang: the page's active language — the server uses it as the
        // email-language fallback for accounts without an explicit setting.
        { token, lang: toDeletionEmailLanguage(lang) },
        {
          skipAuth: true,
          // eslint-disable-next-line @typescript-eslint/no-empty-function -- deliberate: swallow the 401 redirect on the public page
          onUnauthorized: () => {},
        }
      );
      return 'data' in response ? response.data : response;
    },
    onSuccess: ({ deletionEffectiveAt }) => {
      // A logged-in user may open the emailed link in the same browser.
      // SessionValidator syncs only once per mount, so without this
      // write-through Settings/Profile would keep offering "delete" until
      // a full reload. No-ops for logged-out visitors (updateUser guards).
      updateUser({ deletionEffectiveAt });
    },
  });
}

/**
 * Stores the Apple re-auth code captured on the public deletion page so the
 * executor can revoke the SIWA authorization (Apple 5.1.1(v)). PUBLIC like
 * the confirm mutation — never bounce to login.
 */
export function useAppleRevocationMutation() {
  const { post } = useApi();

  return useMutation({
    mutationFn: async (input: AppleRevocationRequestDto) => {
      const response = await post<
        AppleRevocationResponseDto,
        AppleRevocationRequestDto
      >('/user/deletion-apple-revocation', input, {
        skipAuth: true,
        // eslint-disable-next-line @typescript-eslint/no-empty-function -- deliberate: swallow the 401 redirect on the public page
        onUnauthorized: () => {},
      });
      return 'data' in response ? response.data : response;
    },
  });
}
